/**
 * InsightsPanel — AI insights on the painel, aligned to the prototype
 * (PBI #4456).
 *
 * The card follows `.insight-card` from the prototype: severity chip, a
 * "Ver mais" button that opens the details modal, title, description, one
 * recommendation line, and the two actions.
 *
 * Two things this fixes along the way, both required by the PBI's clarity
 * criteria: the severity tag used to be hardcoded to "Atenção" for every
 * card regardless of what the API returned, and the panel carried MUI
 * (`Box`, `Typography`, `CircularProgress`, `Drawer`, `IconButton`) inside
 * a shell that is plain markup everywhere else.
 *
 * "Conversar com IA" no longer mounts a second chat. It opens the lateral
 * assistant the shell already renders (`AppShell` → `<ChatAssistant
 * floating />`), handing it the insight as context — one chat, as in the
 * prototype, but with richer context than its seeded sentence.
 *
 * That assistant itself is still the old MUI component and does not match
 * the prototype (no subtitle, different bubbles, avatars it does not have,
 * a hand-written brand gradient). It is shared by the whole app through the
 * floating button, so restyling it is a component rewrite, not a colour
 * pass — deliberately left to its own PBI. What this one owns is the panel,
 * the card and the details modal.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppToast from '../common/AppToast';
import insightsService from '../../services/insightsService';
import chatLauncher from './chatLauncher';
import InsightDetailModal from './InsightDetailModal';
import { severityTone } from './insightPresentation';

const InsightsPanel = ({ onChatWithInsight, maxItems = 4 }) => {
  const { t } = useTranslation();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailInsight, setDetailInsight] = useState(null);
  const [toast, setToast] = useState(null);

  /**
   * Fallback for when the API is unreachable — four identical placeholder
   * cards. It is a safety net, not the feature: the real content comes from
   * `insightsService`.
   */
  const getMockInsights = useCallback(() => {
    const actions = t('insights.mock.scheduleReview.actions', { returnObjects: true });

    return Array.from({ length: 4 }, (_, index) => ({
      insight_id: `mock-${index + 1}`,
      severity: 'warning',
      category: 'schedule',
      title: t('insights.mock.scheduleReview.title'),
      description: t('insights.mock.scheduleReview.description'),
      recommended_actions: Array.isArray(actions) ? actions : [],
      related_metrics: {},
    }));
  }, [t]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    try {
      const data = await insightsService.getInsights({ status: 'active', limit: maxItems });

      if (!data.insights || data.insights.length === 0) {
        try {
          const generated = await insightsService.generateInsights(30);
          setInsights(generated.insights?.length ? generated.insights : []);
        } catch {
          setInsights([]);
        }
      } else {
        setInsights(data.insights);
      }
    } catch {
      setInsights(getMockInsights());
    } finally {
      setLoading(false);
    }
  }, [getMockInsights, maxItems]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  const handleDismiss = async (insight) => {
    setDetailInsight(null);
    try {
      await insightsService.acknowledgeInsight(insight.insight_id);
    } catch {
      // The card still leaves the panel: acknowledging is advisory, and
      // keeping a dismissed insight on screen is worse than a lost ack.
    }
    setInsights((prev) => prev.filter((item) => item.insight_id !== insight.insight_id));
  };

  const handleChat = (insight) => {
    setDetailInsight(null);
    if (onChatWithInsight) {
      onChatWithInsight(insight);
      return;
    }
    // The launcher answers false when no lateral chat is mounted, which
    // happens wherever this panel renders outside the AppShell — the
    // `/insights-preview` route does exactly that. Without this the click
    // would do nothing and say nothing.
    if (!chatLauncher.openWithInsight(insight)) {
      setToast({ severity: 'error', message: t('insights.chatUnavailable') });
    }
  };

  return (
    <>
      <div className="insights">
        <div className="insights-header">
          <div className="insights-title">
            <h2>{t('insights.title')}</h2>
            {insights.length > 0 && (
              <span className="badge" data-testid="insights-count">{insights.length}</span>
            )}
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={loadInsights}
            aria-label={t('insights.refresh')}
          >
            <span className="material-symbols-rounded" aria-hidden="true">refresh</span>
          </button>
        </div>

        {loading ? (
          <div className="insights-loading" role="status" aria-live="polite">
            <span className="material-symbols-rounded insights-spinner" aria-hidden="true">
              progress_activity
            </span>
          </div>
        ) : insights.length > 0 ? (
          <div className="insights-grid">
            {insights.map((insight) => (
              <InsightCard
                key={insight.insight_id}
                insight={insight}
                onMore={() => setDetailInsight(insight)}
                onDismiss={() => handleDismiss(insight)}
                onChat={() => handleChat(insight)}
              />
            ))}
          </div>
        ) : (
          <div className="insights-empty is-visible">
            <strong>{t('insights.noInsights')}</strong>
            <p>{t('insights.noInsightsDescription')}</p>
          </div>
        )}
      </div>

      <InsightDetailModal
        insight={detailInsight}
        onClose={() => setDetailInsight(null)}
        onDismiss={handleDismiss}
        onChat={handleChat}
      />

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

const InsightCard = ({ insight, onMore, onDismiss, onChat }) => {
  const { t } = useTranslation();
  const tone = severityTone(insight.severity);
  const [firstAction] = insight.recommended_actions || [];

  return (
    <div className="insight-card" data-testid="insight-card">
      <div className="insight-head">
        <span className={`chip ${tone}`}>{t(`insights.severity.${insight.severity}`)}</span>
        <button
          type="button"
          className="insight-more-link"
          onClick={onMore}
          aria-label={t('insights.detail.moreAria', { title: insight.title })}
        >
          {t('insights.detail.more')}
        </button>
      </div>

      <strong className="insight-title">{insight.title}</strong>
      <p className="insight-desc">{insight.description}</p>

      {firstAction ? (
        <p className="insight-action">
          <span className="material-symbols-rounded" aria-hidden="true">lightbulb</span>
          <span>{firstAction}</span>
        </p>
      ) : null}

      <div className="insight-card-actions">
        <button
          type="button"
          className="btn outline insight-action-btn"
          onClick={onDismiss}
          data-testid="insight-dismiss"
        >
          <span className="material-symbols-rounded" aria-hidden="true">close</span>
          {t('insights.dismiss')}
        </button>
        <button
          type="button"
          className="btn primary insight-action-btn"
          onClick={onChat}
          data-testid="insight-chat"
        >
          <span className="material-symbols-rounded" aria-hidden="true">forum</span>
          {t('insights.chat')}
        </button>
      </div>
    </div>
  );
};

export default InsightsPanel;
